import { Injectable } from '@angular/core';
import { NavigationStart, Router, Scroll } from '@angular/router';
import { NavItemDto } from '@infra/nav/dto/nav-item.dto';
import { AuthService } from '@infra/auth/auth.service';
import { NotificacaoRepository } from '@app/data/notificacao/notificacao.repository';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  public current: NavItemDto | null = null;
  public isMobileMenuOpen = false;

  public toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  public closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  public readonly items: NavItemDto[] = [
    {
      id: 'dashboard',
      route: '/dashboard',
      name: 'Visão Geral',
      icon: 'home_outline',
      visible: false,
      protected: true,
    },
    {
      id: 'atendimentos',
      route: '/atendimentos',
      name: 'Atendimentos',
      icon: 'person_outline',
      visible: false,
      protected: true,
    },
    {
      id: 'atualizacao-km',
      route: '/atualizacao-km',
      name: 'Alterar Km',
      icon: 'cached',
      visible: false,
      protected: true,
    },
    {
      id: 'checklist',
      route: '/inspecoes',
      name: 'Inspeções (Checklists)',
      icon: 'fact_check',
      visible: false,
      protected: true,
    },
    {
      id: 'condutor',
      route: '/condutores',
      name: 'Condutores',
      icon: 'co_present',
      visible: false,
      protected: true,
    },
    {
      id: 'frota',
      route: '/frota-contratada',
      name: 'Frota Contratada',
      icon: 'drive_eta',
      visible: false,
      protected: true,
    },
    {
      id: 'faturamento',
      route: '/faturamento',
      name: 'Faturamento',
      icon: 'attach_money',
      visible: false,
      protected: true,
    },
    {
      id: 'medicao',
      route: '/medicoes',
      name: 'Medições',
      icon: 'view_list',
      visible: false,
      protected: true,
    },
    {
      id: 'multas',
      route: '/multas',
      name: 'Multas',
      icon: 'content_paste',
      visible: false,
      protected: true,
    },
    {
      id: 'servicos-executados',
      route: '/servicos-executados',
      name: 'Serviços Executados',
      icon: 'handyman',
      visible: false,
      protected: true,
    },
    {
      id: 'pneus',
      route: '/pneus',
      name: 'Pneus',
      icon: 'album',
      visible: false,
      protected: true,
    },
    {
      id: 'notificacoes',
      route: '/notificacoes',
      name: 'Notificações',
      icon: 'notifications',
      visible: false,
      protected: true,
      quantidadeNotificacoes: 0,
    },
    {
      id: 'minha-conta',
      route: '/minha-conta',
      name: 'Minha Conta',
      icon: 'person',
      visible: false,
    },
  ];

  public getFormId(route: string) {
    const item = this.items.find((item) => item.route === route);
    return item ? item.id : null;
  }

  public get visibleItems(): NavItemDto[] {
    return this.items.filter((item) => item.visible !== false);
  }

  constructor(
    router: Router,
    private authService: AuthService,
    private notificacaoRepository: NotificacaoRepository,
  ) {
    router.events.forEach((event) => {
      if (event instanceof Scroll) {
        this.validatePermissions();
      }

      if (event instanceof NavigationStart) {
        this.activeCurrent(event.url);
      }
    });
  }

  public validatePermissions(): void {
    for (const item of this.items) {
      if (item.protected) {
        item.visible = this.authService.hasPermission(item.id, 'CONSULTAR');
      }
    }
  }

  public activeCurrent(url: string): void {
    for (const item of this.items) {
      if (item.route === url) {
        item.active = true;
        this.current = item;
      } else {
        item.active = false;
      }
    }
  }

  public find(id: string): NavItemDto | null {
    for (const item of this.items) {
      if (item.id === id) {
        return item;
      }

      if (item.children) {
        const path = this.find(id);

        if (path) {
          return path;
        }
      }
    }

    return null;
  }

  public search(name: string): NavItemDto[] {
    const items: NavItemDto[] = [];

    for (const item of this.items) {
      if (!item.active || !item.name) continue;

      if (item.name.includes(name)) {
        items.push(item);
      }

      if (item.children) {
        const path = this.search(name);

        if (path) {
          items.push(...path);
        }
      }
    }

    return items;
  }

  public disable(id: string | null, child?: NavItemDto): void {
    const item = id ? this.find(id) : child;

    if (!item) return;

    item.active = false;

    if (item.children) {
      for (const child of item.children) {
        this.disable(null, child);
      }
    }
  }

  public enable(id: string | null, child?: NavItemDto): void {
    const item = id ? this.find(id) : child;

    if (!item) return;

    item.active = true;

    if (item.children) {
      for (const child of item.children) {
        this.enable(null, child);
      }
    }
  }

  public verificaQuantidadeNotificacoes(): void {
    this.notificacaoRepository.getQuantidadeNotificacoes().subscribe({
      next: (data: any) => {
        const item = this.find('notificacoes');
        if (item) item.quantidadeNotificacoes = data.data.quantidade;
      },
      error: () => {
        console.log('Erro ao recuperar notificações novas');
      },
    });
  }
}
